/**
 * 订单详情抽屉 - 订单明细列表区块
 *
 * 以表格形式展示 orderItems（名称/规格/数量/每日小计，单价量纲为每日），底部展示
 * 总项数、每日小计 × 天数（多天活动）与合计金额。受控组件，不持有业务状态。
 */
import { roundMoney } from '../../../../../utils/paymentAmount';
import type { OrderItemDetail } from '../types';

interface Props {
  items: OrderItemDetail[];
  itemsSubtotalPerDay: number;
  numDays: number | null;
  itemsTotal: number;
}

export default function OrderItemsSection({ items, itemsSubtotalPerDay, numDays, itemsTotal }: Props) {
  return (
    <>
      {/* Order items */}
      {items.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">订单明细</p>
          <div className="bg-gray-50 rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-gray-100 text-xs text-gray-500 font-medium">
              <span className="col-span-4">名称</span><span className="col-span-3">规格</span><span className="col-span-2 text-center">数量</span><span className="col-span-3 text-right">每日小计</span>
            </div>
            {items.map((item, idx) => {
              const subtotal = roundMoney(Number(item.quantity ?? 0) * Number(item.unitPrice ?? 0));
              return (
                <div key={item.id ?? idx} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-t border-gray-100 text-sm">
                  <span className="col-span-4 text-gray-800 font-medium truncate">{item.itemName ?? '-'}</span>
                  <span className="col-span-3 text-gray-500 truncate">{item.itemSpec || '-'}</span>
                  <span className="col-span-2 text-center text-gray-600">{item.quantity ?? '-'}</span>
                  <span className="col-span-3 text-right text-gray-700 font-medium">¥{subtotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                </div>
              );
            })}
            <div className="flex flex-col gap-1 px-4 py-3 border-t-2 border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">共 {items.length} 项</span>
                {numDays !== null && numDays > 1 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">每日 ¥{itemsSubtotalPerDay.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                    <span className="text-xs text-amber-500 font-medium flex items-center gap-0.5">
                      <i className="ri-calendar-2-line text-xs" />
                      × {numDays}天
                    </span>
                    <span className="text-base font-bold text-teal-600">= ¥{itemsTotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">合计</span>
                    <span className="text-base font-bold text-teal-600">¥{itemsTotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}